import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { LoginContext } from '../../Contexts/LoginContext';
import { showToast } from '../../Components/utils/ShowToast';
import { Switch } from "@mui/material";
import Loader from '../../Components/utils/Loader';
import { FiInfo } from 'react-icons/fi';

const PaymentMethods = () => {
  const { user, fetchUserData } = useContext(LoginContext);
  const [activeMethod, setActiveMethod] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentModal, setCurrentModal] = useState(null);
  const [formData, setFormData] = useState({
    stripeId: '',
    wiseEmail: '',
    paypalEmail: ''
  });
  const [loading, setLoading] = useState(false);
  const [stripeAccountStatus, setStripeAccountStatus] = useState(null);
  const [restrictions, setRestrictions] = useState(null);

  useEffect(() => {
    if (user?.sellerId) {
      setActiveMethod(user.sellerId.activePaymentMethod);
      setFormData({
        stripeId: '',
        wiseEmail: user.sellerId.wiseEmail || '',
        paypalEmail: user.sellerId.paypalEmail || ''
      });
      if (user.sellerId.stripeAccountId) {
        const fetchStripeStatus = async () => {
          try {
            const response = await axios.get(
              `${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/seller/stripe-account-status`,
              { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            setStripeAccountStatus(response.data.status);
          } catch (error) {
            console.error('Failed to fetch Stripe account status', error);
          }
        };
        fetchStripeStatus();
      }
    }

    const fetchRestrictions = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/restrictions`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.data.success) setRestrictions(res.data.restriction);
      } catch (error) {
        console.error('Failed to fetch restrictions', error);
      }
    };
    fetchRestrictions();
  }, [user]);

  const isMethodDisabledByAdmin = (methodKey) => {
    if (!restrictions?.paymentMethods) return false;
    return !restrictions.paymentMethods[methodKey];
  };

  const isMethodRestrictedByCountry = (methodKey) => {
    if (!restrictions?.restrictedCountries?.[methodKey]) return false;
    const sellerCountryCode = user?.sellerId?.sellerAddress?.country?.code;
    if (!sellerCountryCode) return false;
    return restrictions.restrictedCountries[methodKey].some(c => c.code === sellerCountryCode);
  };

  const getMethodRestrictionMessage = (methodKey, methodLabel) => {
    if (isMethodDisabledByAdmin(methodKey)) return `${methodLabel} has been disabled by the administrator.`;
    if (isMethodRestrictedByCountry(methodKey)) return `${methodLabel} is not available in your country.`;
    return null;
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const updateActivePaymentMethod = async (method) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/seller/update-active-payment-method`,
        { activePaymentMethod: method },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (response.data.success) {
        showToast('Active payment method updated!', 'success');
        setActiveMethod(method);
        await fetchUserData();
      }
    } catch (error) {
      console.log(error);
      showToast(error.response?.data?.message || 'Failed to update active payment method', 'error');
    }
  };

  const handleStripeToggle = () => {
    if (activeMethod === 'Stripe') {
      if (window.confirm('Are you sure you want to turn off Stripe?')) {
        updateActivePaymentMethod(null);
      }
    } else {
      updateActivePaymentMethod('Stripe');
    }
  };

  const handleWiseToggle = () => {
    if (activeMethod === 'Wise') {
      if (window.confirm('Are you sure you want to turn off Wise?')) {
        updateActivePaymentMethod(null);
      }
    } else {
      updateActivePaymentMethod('Wise');
    }
  };

  const handlePaypalToggle = () => {
    if (activeMethod === 'Paypal') {
      if (window.confirm('Are you sure you want to turn off Paypal?')) {
        updateActivePaymentMethod(null);
      }
    } else {
      updateActivePaymentMethod('Paypal');
    }
  };

  const connectStripe = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/seller/connect-stripe`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      window.location.href = response.data.url;
    } catch (error) {
      console.log(error)
      showToast(error.response?.data?.message || 'Something went wrong!', 'error');
      setLoading(false);
    }
  };

  const handleRemoveStripeAccount = async () => {
    if (window.confirm('Are you sure you want to remove the incomplete Stripe account?')) {
      try {
        await axios.post(
          `${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/seller/remove-stripe-account`,
          {},
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        setStripeAccountStatus(null);
        showToast('Stripe account removed successfully', 'success');
        await fetchUserData();
      } catch (error) {
        showToast('Failed to remove Stripe account', 'error');
      }
    }
  };

  const handleModalOpen = (method) => {
    setCurrentModal(method);
    setModalOpen(true);
  };

  const addWise = async () => {
    if (!formData.wiseEmail) {
      showToast('Please enter Wise Email!', 'error');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/seller/add-wise`,
        { wiseEmail: formData.wiseEmail },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (response.data.success) {
        showToast('Wise account added successfully!', 'success');
        setModalOpen(false);
        await fetchUserData();
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Something went wrong!', 'error');
    } finally {
      setLoading(false);
    }
  };

  const addPaypal = async () => {
    if (!formData.paypalEmail) {
      showToast('Please enter Paypal Email!', 'error');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/seller/add-paypal`,
        { paypalEmail: formData.paypalEmail },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (response.data.success) {
        showToast('Paypal account added successfully!', 'success');
        setModalOpen(false);
        await fetchUserData();
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Something went wrong!', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (currentModal === 'Wise') {
      addWise();
    } else if (currentModal === 'Paypal') {
      addPaypal();
    }
  };

  const modalContent = {
    Stripe: {
      title: 'Add Stripe',
      field: 'stripeId',
      label: 'Stripe ID',
      warning: 'Make sure you enter correct Stripe ID, otherwise you may not receive your funds!'
    },
    Wise: {
      title: 'Add Wise',
      field: 'wiseEmail',
      label: 'Wise Email',
      warning: 'Make sure you enter correct Wise Email, otherwise you may not receive your funds!'
    },
    Paypal: {
      title: 'Add Paypal',
      field: 'paypalEmail',
      label: 'Paypal Email',
      warning: 'Make sure you enter correct Paypal Email, otherwise you may not receive your funds!'
    }
  };

  return (
    <div className="paymentMethodContainer">
      <h3 className="paymentMethodTitle">Payment Methods</h3>

      <div className="paymentMethods">
        {/* Stripe */}
        <div className={`paymentMethodItem ${getMethodRestrictionMessage('stripe', 'Stripe') ? 'methodDisabled' : ''}`}>
          <div className="paymentMethodLeft">
            <div className="paymentMethodIcon">S</div>
            <span className="paymentMethodName">Stripe</span>
          </div>
          <div className="paymentMethodRight">
            <Switch
              checked={activeMethod === 'Stripe'}
              onChange={handleStripeToggle}
              color="primary"
              disabled={!user?.sellerId?.stripeAccountId || stripeAccountStatus !== 'complete' || !!getMethodRestrictionMessage('stripe', 'Stripe')}
            />
            {user?.sellerId?.stripeAccountId && stripeAccountStatus === 'complete' ? (
              <button className="modalButton submit" disabled>Connected</button>
            ) : stripeAccountStatus === 'restricted' || stripeAccountStatus === 'pending' ? (
              <>
                <button className="modalButton cancel" onClick={handleRemoveStripeAccount} disabled={loading || !!getMethodRestrictionMessage('stripe', 'Stripe')}>
                  Remove Incomplete Account
                </button>
                <button className="modalButton submit" onClick={connectStripe} disabled={loading || !!getMethodRestrictionMessage('stripe', 'Stripe')}>
                  {loading ? 'Loading...' : 'Continue Linking'}
                </button>
              </>
            ) : (
              <button className="modalButton submit" onClick={connectStripe} disabled={loading || !!getMethodRestrictionMessage('stripe', 'Stripe')}>
                {loading ? <Loader type="miniPlus" /> : 'Connect'}
              </button>
            )}
          </div>
          {getMethodRestrictionMessage('stripe', 'Stripe') && (
            <div className="methodRestrictionInfo"><FiInfo className="infoIcon2" /> {getMethodRestrictionMessage('stripe', 'Stripe')}</div>
          )}
        </div>

        {/* Wise */}
        <div className={`paymentMethodItem ${getMethodRestrictionMessage('wise', 'Wise') ? 'methodDisabled' : ''}`}>
          <div className="paymentMethodLeft">
            <div className="paymentMethodIcon">W</div>
            <span className="paymentMethodName">Wise</span>
          </div>
          <div className="paymentMethodRight">
            <Switch
              checked={activeMethod === 'Wise'}
              onChange={handleWiseToggle}
              color="primary"
              disabled={!user?.sellerId?.wiseEmail || !!getMethodRestrictionMessage('wise', 'Wise')}
            />
            <button
              className={`modalButton ${user?.sellerId?.wiseEmail ? 'cancel' : 'submit'}`}
              onClick={() => handleModalOpen('Wise')}
              disabled={!!getMethodRestrictionMessage('wise', 'Wise')}
            >
              {user?.sellerId?.wiseEmail ? 'Edit Details' : 'Add Wise'}
            </button>
          </div>
          {getMethodRestrictionMessage('wise', 'Wise') && (
            <div className="methodRestrictionInfo"><FiInfo className="infoIcon2" /> {getMethodRestrictionMessage('wise', 'Wise')}</div>
          )}
        </div>

        {/* Paypal */}
        <div className={`paymentMethodItem ${getMethodRestrictionMessage('paypal', 'PayPal') ? 'methodDisabled' : ''}`}>
          <div className="paymentMethodLeft">
            <div className="paymentMethodIcon">P</div>
            <span className="paymentMethodName">Paypal</span>
          </div>
          <div className="paymentMethodRight">
            <Switch
              checked={activeMethod === 'Paypal'}
              onChange={handlePaypalToggle}
              color="primary"
              disabled={!user?.sellerId?.paypalEmail || !!getMethodRestrictionMessage('paypal', 'PayPal')}
            />
            <button
              className={`modalButton ${user?.sellerId?.paypalEmail ? 'cancel' : 'submit'}`}
              onClick={() => handleModalOpen('Paypal')}
              disabled={!!getMethodRestrictionMessage('paypal', 'PayPal')}
            >
              {user?.sellerId?.paypalEmail ? 'Edit Details' : 'Add Paypal'}
            </button>
          </div>
          {getMethodRestrictionMessage('paypal', 'PayPal') && (
            <div className="methodRestrictionInfo"><FiInfo className="infoIcon2" /> {getMethodRestrictionMessage('paypal', 'PayPal')}</div>
          )}
        </div>
      </div>

      {/* Modal */}
      {modalOpen && currentModal && (
        <div className="modalOverlay">
          <div className="modalContent">
            <h2 className="modalTitle">{modalContent[currentModal]?.title}</h2>
            <div className="modalBody">
              <label className="modalLabel">
                {modalContent[currentModal].label}
              </label>
              <input
                type="text"
                name={modalContent[currentModal].field}
                value={formData[modalContent[currentModal].field]}
                onChange={handleInputChange}
                className="inputField"
                placeholder={`Enter your ${modalContent[currentModal].label}`}
              />
              <p className="modalWarning">
                {modalContent[currentModal].warning}
              </p>
              <div className="modalActions">
                <button
                  className="modalButton cancel"
                  onClick={() => setModalOpen(false)}
                  disabled={loading}
                >
                  Close
                </button>
                <button className="modalButton submit" onClick={handleSave} disabled={loading}>
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethods;